import { debounce } from 'lodash';
import { useRouter } from 'next/router';
import {
  useEffect,
  useCallback,
  RefObject,
  useState,
  useRef,
  useMemo,
} from 'react';
import { useIntersection } from 'react-use';
import { onINP } from 'web-vitals/attribution';
import { Session } from '@silvertours/front-shared';
import {
  trackTypoDomainRedirect,
  trackCriteo,
  trackPageView,
  trackWebVitalsMetric,
} from './events';
import { getTrackingDataForPath } from './utils';
import { getAllowedQueryString } from './channelTracking';
import type { DataLayerSettings } from './dataLayer';
import { useSiteInfo } from '../../SiteInfo/hooks/useSiteInfo';
import { SiteSettings } from '../../SiteInfo';
import { checkConsent } from '../../Runtime/hooks/useConsent';

export const mapDataLayerSettings = (
  siteSettings: SiteSettings,
): DataLayerSettings => ({
  environment: siteSettings?.sessionData?.gtmEnvironment,
  anonymizeIP: siteSettings?.sessionData?.anonymizeIP,
  // Default to "not a bot" rather than letting `undefined` through: Tag Manager
  // treats a missing value as false anyway, and being explicit keeps a settings
  // shape without the field from silently suppressing a real visitor's tags.
  isBot: siteSettings?.isBotCall ?? false,
  locale: siteSettings?.language, // re-using "language" here to be consistent with previous implementation
  language: siteSettings?.language,
  market: siteSettings?.market,
});

const hasUcSettings = () => {
  try {
    return localStorage.getItem('uc_settings') !== null;
  } catch {
    return false;
  }
};

type LandingData = {
  queryString: string;
  referrer: string | null;
  /** True if this page view wrote the first data of the session */
  ownsEntry: boolean;
};

/**
 * Store data in session storage used in carspa to track bookings.
 * Write the data only when Usercentrics has written "uc_settings". On a first
 * visit, Usercentrics writes it after mount, so wait for "UC:consentChange".
 * Write again on each consent change, so that the data agrees with the latest
 * consent. Do not change data that an earlier page view in this session wrote.
 *
 * If `filterQuery` is true, store only the query params that the given consent
 * allows. If it is false, store the full query string.
 * The feature toggles load after mount, so write again when `filterQuery` changes.
 */
const useChannelTracking = (filterQuery: boolean) => {
  const landingRef = useRef<LandingData | null>(null);

  useEffect(() => {
    if (!landingRef.current) {
      const {
        location: { search: queryString },
        referrer,
      } = document;

      const ref = new URLSearchParams(queryString).get('ref');

      if (ref) {
        trackTypoDomainRedirect(ref);
      }

      landingRef.current = {
        queryString,
        referrer: referrer || ref,
        ownsEntry: !Session.SessionStorageManager.getData('channel_tracking'),
      };
    }

    const { queryString, referrer, ownsEntry } = landingRef.current;

    if (!ownsEntry) {
      return undefined;
    }

    const storeChannelTracking = () => {
      if (!hasUcSettings()) {
        return;
      }
      Session.SessionStorageManager.setData('channel_tracking', [
        {
          queryString: filterQuery
            ? getAllowedQueryString(queryString, checkConsent)
            : queryString,
          referrer,
        },
      ]);
    };

    storeChannelTracking();
    document.addEventListener('UC:consentChange', storeChannelTracking);

    return () => {
      document.removeEventListener('UC:consentChange', storeChannelTracking);
    };
  }, [filterQuery]);
};

const usePageView = () => {
  const siteInfo = useSiteInfo();
  const router = useRouter();
  useChannelTracking(
    siteInfo.featureToggles?.filterChannelTrackingQuery === true,
  );

  // Initialize the global tracking state
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.GTM_TRACKING_STATE) {
      window.GTM_TRACKING_STATE = {
        trackedPaths: new Set(),
      };
    }
  }, []);

  // Track if we've handled the initial page load
  const initialLoadHandledRef = useRef(false);

  // Create the actual tracking function
  const trackPageViewImpl = useCallback(
    (path: string) => {
      if (!siteInfo.settingsLoaded) {
        return;
      }

      const settings = mapDataLayerSettings(siteInfo.settings);
      const data = getTrackingDataForPath(settings, path);
      trackPageView(data);

      if (!siteInfo.settings.isInternalCall) {
        trackCriteo();
      }
    },
    [siteInfo],
  );

  // Create a debounced version of the tracking function
  const debouncedTrackPageView = useMemo(
    () => debounce(trackPageViewImpl, 300),
    [trackPageViewImpl],
  );

  // Cleanup debounce on unmount
  useEffect(
    () => () => {
      debouncedTrackPageView.cancel();
    },
    [debouncedTrackPageView],
  );

  // Handle initial page view separately
  useEffect(() => {
    if (
      router.isReady &&
      siteInfo.settingsLoaded &&
      !initialLoadHandledRef.current
    ) {
      initialLoadHandledRef.current = true;
      trackPageViewImpl(router.asPath);
    }
  }, [
    router.isReady,
    router.asPath,
    trackPageViewImpl,
    siteInfo.settingsLoaded,
  ]);

  // Handle route changes with debounce
  useEffect(() => {
    const handleRouteChange = (path: string) => {
      debouncedTrackPageView(path);
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, debouncedTrackPageView]);
};

declare global {
  interface Window {
    GTM_TRACKING_STATE?: {
      trackedPaths: Set<string>;
    };
  }
}

const useElementScrolledToView = (
  ref: RefObject<HTMLElement>,
  callback: (...args: any[]) => void,
) => {
  const [seen, setSeen] = useState(false);
  const intersection = useIntersection(ref, {
    root: null,
    threshold: 1,
  });

  useEffect(() => {
    if (intersection?.isIntersecting && !seen) {
      callback();
      setSeen(true);
    }
  }, [callback, intersection, seen]);
};

/** @deprecated Use `next/web-vitals` when Next.js 13+ is available. */
const useReportWebVitals = () => {
  useEffect(() => {
    onINP(trackWebVitalsMetric, { reportAllChanges: true });
    // onLCP(trackWebVitalsMetric);
    // onCLS(trackWebVitalsMetric);
  });
};

export { usePageView, useElementScrolledToView, useReportWebVitals };
